import React from 'react';
import { Box, Typography, IconButton, Modal, Divider, Link as MuiLink } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const modalSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95vw', sm: '80vw', md: '800px' },
  maxHeight: '85vh',
  overflowY: 'auto' as const,
  background: '#141210',
  border: '1px solid var(--color-border-hover)',
  borderRadius: 2,
  p: { xs: 3, md: 5 },
  outline: 'none',
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="h6"
    sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-accent)', mb: 1.5, mt: 3, fontSize: '1.05rem' }}
  >
    {children}
  </Typography>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.9, mb: 1.2 }}>
    {children}
  </Typography>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, mb: 1.2 }}>
    {items.map((item) => (
      <Typography key={item} component="li" variant="body2" sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.9 }}>
        {item}
      </Typography>
    ))}
  </Box>
);

interface Props {
  open: boolean;
  onClose: () => void;
}

const PrivacyModal: React.FC<Props> = ({ open, onClose }) => (
  <Modal open={open} onClose={onClose}>
    <Box sx={modalSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h5" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)' }}>
          Política de Privacidad
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'var(--color-text-secondary)', mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Para>Última actualización: mayo de 2026</Para>

      <SectionTitle>1. Responsable del tratamiento</SectionTitle>
      <Para>
        El responsable del tratamiento de los datos personales recabados a través del sitio web accesible desde el
        dominio <strong style={{ color: 'var(--color-text-primary)' }}>www.sushigi-catering.com</strong> es:
      </Para>
      <BulletList items={[
        'Razón social: Terrasuyi, S.L.',
        'CIF: B-88027701',
        'Domicilio: C/ Juan Carlos I, n.º 13, local 19, 28806 Alcalá de Henares, Madrid, España',
        'Correo electrónico: sushigi.catering@gmail.com',
      ]} />

      <SectionTitle>2. Datos que recabamos y finalidad</SectionTitle>
      <Para>A través del formulario de contacto recabamos los siguientes datos personales:</Para>
      <BulletList items={[
        'Nombre y apellidos — para identificar al solicitante',
        'Dirección de correo electrónico — para responder a la solicitud',
        'Teléfono — para contacto directo si es necesario',
        'Tipo de evento, servicio, fecha y localización — para elaborar un presupuesto personalizado',
        'Comentarios adicionales — para entender mejor sus necesidades',
      ]} />
      <Para>
        Estos datos se utilizan exclusivamente para{' '}
        <strong style={{ color: 'var(--color-text-primary)' }}>atender su solicitud de presupuesto o información</strong>{' '}
        y para el envío de comunicaciones comerciales relacionadas con nuestros servicios, siempre que haya prestado
        su consentimiento expreso.
      </Para>

      <SectionTitle>3. Base jurídica del tratamiento</SectionTitle>
      <Para>
        El tratamiento se ampara en las siguientes bases legitimadoras conforme al art. 6 del Reglamento (UE) 2016/679
        (RGPD) y la Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los
        derechos digitales (LOPD-GDD):
      </Para>
      <BulletList items={[
        'El consentimiento inequívoco del interesado (art. 6.1.a RGPD) para el envío de comunicaciones comerciales.',
        'La ejecución de medidas precontractuales a petición del interesado (art. 6.1.b RGPD) para responder a solicitudes de presupuesto.',
        'El interés legítimo del responsable (art. 6.1.f RGPD) en la gestión de consultas recibidas.',
      ]} />

      <SectionTitle>4. Plazo de conservación</SectionTitle>
      <Para>
        Los datos se conservarán durante el tiempo necesario para atender su solicitud y, en su caso, durante el
        periodo en que pueda derivarse responsabilidad por la relación establecida. En caso de comunicaciones
        comerciales, hasta que revoque su consentimiento.
      </Para>

      <SectionTitle>5. Comunicaciones comerciales</SectionTitle>
      <Para>
        De conformidad con el{' '}
        <strong style={{ color: 'var(--color-text-primary)' }}>
          artículo 21 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de
          Comercio Electrónico (LSSI-CE)
        </strong>
        , solo le enviaremos comunicaciones publicitarias o promocionales si ha prestado su consentimiento expreso.
        Puede revocarlo en cualquier momento escribiendo a sushigi.catering@gmail.com.
      </Para>

      <SectionTitle>6. Encargados del tratamiento</SectionTitle>
      <Para>
        Para el envío de las comunicaciones derivadas del formulario de contacto utilizamos el servicio{' '}
        <strong style={{ color: 'var(--color-text-primary)' }}>EmailJS</strong> (EmailJS Ltd.), que actúa como
        encargado del tratamiento conforme al art. 28 del RGPD. No cedemos sus datos a terceros para fines propios,
        ni los transferimos fuera del Espacio Económico Europeo salvo con garantías adecuadas.
      </Para>

      <SectionTitle>7. Sus derechos</SectionTitle>
      <Para>
        Como interesado, puede ejercer en cualquier momento los siguientes derechos ante Terrasuyi, S.L. dirigiendo
        un escrito a la dirección postal o electrónica indicada en el apartado 1, adjuntando copia de su DNI u
        otro documento identificativo:
      </Para>
      <BulletList items={[
        'Acceso — conocer qué datos tratamos sobre usted.',
        'Rectificación — solicitar la corrección de datos inexactos.',
        'Supresión («derecho al olvido») — solicitar la eliminación de sus datos.',
        'Limitación del tratamiento — solicitar que se restrinja el uso de sus datos.',
        'Portabilidad — recibir sus datos en formato estructurado y de uso común.',
        'Oposición — oponerse al tratamiento basado en interés legítimo o con fines de marketing directo.',
        'Retirada del consentimiento — en cualquier momento, sin efecto retroactivo.',
      ]} />
      <Para>
        Si considera que el tratamiento de sus datos vulnera la normativa vigente, puede presentar una reclamación
        ante la{' '}
        <strong style={{ color: 'var(--color-text-primary)' }}>
          Agencia Española de Protección de Datos (AEPD)
        </strong>{' '}
        en{' '}
        <MuiLink
          href="https://www.aepd.es"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'var(--color-accent)', textDecorationColor: 'var(--color-accent)' }}
        >
          www.aepd.es
        </MuiLink>
        .
      </Para>

      <Divider sx={{ my: 3, borderColor: 'var(--color-border)' }} />

      <Typography variant="h5" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)', mb: 2 }}>
        Política de Tratamiento de Alimentos
      </Typography>

      <Para>
        En <strong style={{ color: 'var(--color-text-primary)' }}>Terrasuyi, S.L.</strong> cumplimos con todas las
        normativas vigentes en materia de seguridad alimentaria y manipulación de alimentos, conforme a la{' '}
        <strong style={{ color: 'var(--color-text-primary)' }}>
          Ley 17/2011, de 5 de julio, de seguridad alimentaria y nutrición
        </strong>{' '}
        y el{' '}
        <strong style={{ color: 'var(--color-text-primary)' }}>
          Reglamento (CE) n.º 852/2004 del Parlamento Europeo
        </strong>{' '}
        relativo a la higiene de los productos alimenticios.
      </Para>
      <Para>Nuestros procesos siguen estrictas medidas de control para garantizar la seguridad y calidad en cada evento:</Para>
      <BulletList items={[
        'Instalaciones certificadas que cumplen con los requisitos de higiene y seguridad alimentaria.',
        'Todo el personal dispone del Certificado de Manipulador de Alimentos en vigor.',
        'Mantenimiento de la cadena de frío durante todo el proceso de preparación y entrega.',
        'Controles periódicos de calidad, trazabilidad e higiene en cada servicio.',
        'Información clara y detallada sobre alérgenos conforme al Reglamento (UE) n.º 1169/2011.',
      ]} />
      <Para>
        Para cualquier consulta sobre nuestras políticas de seguridad alimentaria, puede contactar con nosotros en{' '}
        <MuiLink
          href="mailto:sushigi.catering@gmail.com"
          sx={{ color: 'var(--color-accent)', textDecorationColor: 'var(--color-accent)' }}
        >
          sushigi.catering@gmail.com
        </MuiLink>{' '}
        o por escrito a C/ Juan Carlos I, n.º 13, local 19, 28806 Alcalá de Henares, Madrid, España.
      </Para>
    </Box>
  </Modal>
);

export default PrivacyModal;
